<template>
  <div>
    <!-- Header con información de la lista -->
    <b-card class="mb-2">
      <b-row>
        <b-col
          cols="12"
          md="8"
        >
          <h4 class="mb-1">
            <feather-icon
              icon="TagIcon"
              size="20"
              class="mr-1"
            />
            {{ priceList.name }}
          </h4>
          <p class="text-muted mb-2">
            {{ priceList.description }}
          </p>

          <div class="d-flex flex-wrap">
            <b-badge
              :variant="`light-${ priceList.active ? 'success' : 'danger' }`"
              class="mr-2 mb-1"
            >
              {{ priceList.active ? 'Activa' : 'Inactiva' }}
            </b-badge>

            <b-badge
              v-if="priceList.is_master"
              variant="light-primary"
              class="mr-2 mb-1"
              title="De esta lista sale el precio base de los estilos"
            >
              Maestra
            </b-badge>

            <b-badge
              v-if="priceList.is_default"
              variant="light-info"
              class="mr-2 mb-1"
              title="Se asigna a todo cliente nuevo"
            >
              Por defecto
            </b-badge>

            <b-badge
              :variant="priceList.can_edit ? 'light-warning' : 'light-secondary'"
              class="mr-2 mb-1"
              :title="editLockHint"
            >
              <feather-icon
                :icon="priceList.can_edit ? 'UnlockIcon' : 'LockIcon'"
                size="12"
                class="mr-50"
              />
              {{ priceList.can_edit ? 'Edición habilitada' : 'Solo lectura' }}
            </b-badge>

            <b-badge
              v-if="priceList.product_variants_count"
              variant="light-secondary"
              class="mr-2 mb-1"
            >
              {{ priceList.customized_count }} de {{ priceList.product_variants_count }} variantes con precio propio
            </b-badge>
          </div>
        </b-col>

        <b-col
          cols="12"
          md="4"
          class="text-md-right"
        >
          <b-button
            v-if="priceList.can_edit"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            @click="showEditSidebar = true"
          >
            <feather-icon
              icon="EditIcon"
              size="16"
              class="mr-50"
            />
            Editar
          </b-button>

          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="outline-secondary"
            @click="$emit('closeTab', priceListId, 'view')"
          >
            <feather-icon
              icon="XIcon"
              size="16"
              class="mr-50"
            />
            Cerrar
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Contenido principal -->
    <b-card no-body>
      <!-- Toolbar -->
      <div class="m-2">
        <b-row class="align-items-center">
          <b-col
            cols="12"
            md="7"
            class="d-flex flex-wrap align-items-center mb-1 mb-md-0"
          >
            <div class="toolbar-field mr-2">
              <label class="toolbar-field__label">Estado del precio</label>
              <v-select
                v-model="customizedFilter"
                :options="customizedOptions"
                :reduce="opt => opt.value"
                :clearable="false"
                :searchable="false"
                label="label"
                class="toolbar-field__select toolbar-field__select--state"
              />
            </div>

            <div class="toolbar-field">
              <label class="toolbar-field__label">Mostrar</label>
              <v-select
                v-model="perPage"
                :options="perPageOptions"
                :clearable="false"
                :searchable="false"
                :disabled="!paginado"
                :title="paginado ? '' : 'Con búsqueda o filtro se muestran todas las coincidencias'"
                class="toolbar-field__select toolbar-field__select--per-page"
              />
            </div>
          </b-col>

          <b-col
            cols="12"
            md="5"
          >
            <b-form-input
              v-model="searchQuery"
              placeholder="Buscar productos..."
            />
          </b-col>
        </b-row>

        <b-row class="mt-1">
          <b-col
            cols="12"
            class="d-flex align-items-center justify-content-end"
          >
            <span class="text-muted">
              {{ productVariantsMeta ? `Mostrando del ${productVariantsMeta.from} al ${productVariantsMeta.to} de ${productVariantsMeta.total} registros` : 'Cargando...' }}
            </span>
          </b-col>
        </b-row>
      </div>

      <!--
        Oferta masiva. Aparece al seleccionar filas: el caso normal es buscar un
        estilo, seleccionar sus variantes y aplicarles la misma oferta y vigencia.
      -->
      <div
        v-if="priceList.can_edit"
        class="mx-2 mb-1"
      >
        <div
          v-if="selectedRows.length > 0"
          class="bulk-panel mb-1"
        >
          <div class="bulk-panel__header">
            <h4 class="bulk-panel__title">
              {{ selectedRows.length }}
              {{ selectedRows.length === 1 ? 'variante seleccionada' : 'variantes seleccionadas' }}
            </h4>
            <p class="bulk-panel__subtitle">
              Aplica una oferta o actualiza los precios de las variantes seleccionadas.
            </p>
          </div>

          <!-- Oferta: mismo precio y vigencia para todo lo seleccionado. -->
          <div class="bulk-section">
            <div class="bulk-section__intro">
              <span class="bulk-section__icon">
                <feather-icon
                  icon="TagIcon"
                  size="20"
                />
              </span>
              <div>
                <h5 class="bulk-section__title">
                  Aplicar oferta
                </h5>
                <p class="bulk-section__text">
                  Define un precio ofertado y un rango de fechas para las variantes seleccionadas.
                </p>
              </div>
            </div>

            <div class="bulk-section__form">
              <div class="bulk-field">
                <label>Precio ofertado</label>
                <b-form-input
                  v-model.number="bulkOffer.price"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="vacío = quitar"
                />
              </div>

              <div class="bulk-field">
                <label>Desde</label>
                <b-form-input
                  v-model="bulkOffer.startsAt"
                  type="date"
                  @focus="openDatePicker"
                  @click="openDatePicker"
                />
              </div>

              <div class="bulk-field">
                <label>Hasta</label>
                <b-form-input
                  v-model="bulkOffer.endsAt"
                  type="date"
                  title="Vacío = sin vencimiento"
                  @focus="openDatePicker"
                  @click="openDatePicker"
                />
              </div>

              <div class="bulk-section__actions">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  :disabled="isApplyingBulkOffer"
                  @click="applyBulkOffer"
                >
                  <b-spinner
                    v-if="isApplyingBulkOffer"
                    small
                    class="mr-50"
                  />
                  Aplicar oferta
                </b-button>

                <b-button
                  variant="outline-secondary"
                  title="Quitar la selección"
                  @click="clearSelection"
                >
                  Limpiar
                </b-button>
              </div>
            </div>
          </div>

          <!--
            Precios en bloque. El precio base es del catalogo (estilo + color) y
            afecta a todas las listas; por eso solo lo ve administracion. El
            precio en la lista es solo de esta lista.
          -->
          <div class="bulk-section">
            <div class="bulk-section__intro">
              <span class="bulk-section__icon">
                <feather-icon
                  icon="DollarSignIcon"
                  size="20"
                />
              </span>
              <div>
                <h5 class="bulk-section__title">
                  Precios
                </h5>
                <p class="bulk-section__text">
                  Actualiza {{ isAdmin ? 'el precio base y ' : '' }}el precio en la lista de las variantes seleccionadas.
                </p>
              </div>
            </div>

            <div class="bulk-section__form">
              <div
                v-if="isAdmin"
                class="bulk-field"
              >
                <label>Precio base</label>
                <b-form-input
                  v-model.number="bulkPrices.base"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="sin cambio"
                />
              </div>

              <div class="bulk-field">
                <label>Precio en la lista</label>
                <b-form-input
                  v-model.number="bulkPrices.list"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="sin cambio"
                />
              </div>

              <div class="bulk-section__actions">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  :disabled="isApplyingBulkPrices"
                  @click="applyBulkPrices"
                >
                  <b-spinner
                    v-if="isApplyingBulkPrices"
                    small
                    class="mr-50"
                  />
                  Aplicar precios
                </b-button>
              </div>
            </div>
          </div>

          <!--
            Lo acumulado, incluso de busquedas anteriores: permite juntar
            variantes de varios estilos en una misma oferta.
          -->
          <div class="bulk-selected">
            <span class="bulk-section__icon bulk-section__icon--small">
              <feather-icon
                icon="PackageIcon"
                size="16"
              />
            </span>
            <div class="bulk-selected__body">
              <h6 class="bulk-selected__title">
                Variantes seleccionadas ({{ selectedRows.length }})
              </h6>
              <div class="bulk-selected__chips">
                <span
                  v-for="row in selectedRows"
                  :key="row.id"
                  class="bulk-chip"
                >
                  {{ row.product_variant.sku }}
                  <feather-icon
                    icon="XIcon"
                    size="14"
                    class="bulk-chip__remove"
                    title="Quitar de la selección"
                    @click="removeFromSelection(row.id)"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>

        <b-button
          v-if="productVariantsData.length"
          variant="flat-primary"
          size="sm"
          @click="selectAllVisible"
        >
          <feather-icon
            icon="CheckSquareIcon"
            size="14"
            class="mr-50"
          />
          Seleccionar las {{ productVariantsData.length }} filas visibles
        </b-button>

        <small
          v-if="selectedRows.length"
          class="text-muted ml-1"
        >
          La selección se conserva al buscar otro estilo.
        </small>
      </div>

      <!-- Tabla de productos -->
      <b-table
        ref="variantsTable"
        class="position-relative price-list-variants"
        hover
        responsive
        small
        :busy="tableIsBusy"
        primary-key="id"
        show-empty
        empty-text="No se encontraron productos en esta lista de precios"
        :items="productVariantsData"
        :fields="productVariantsFields"
        :selectable="priceList.can_edit"
        select-mode="multi"
        selected-variant=""
        @row-selected="onRowsSelected"
      >
        <template #table-busy>
          <div class="text-center text-dark my-2">
            <b-spinner class="align-middle" />
          </div>
        </template>

        <!-- Column: Product -->
        <template #cell(product_name)="data">
          <div class="font-weight-bold text-primary d-flex align-items-center">
            <!-- Identificador de fila seleccionada (el fondo queda blanco). -->
            <feather-icon
              v-if="data.rowSelected"
              icon="CheckCircleIcon"
              size="16"
              class="text-primary mr-50"
            />
            {{ data.item.product_variant.name }}
          </div>
          <small class="text-muted">{{ data.item.product_variant.product.description }}</small>
        </template>

        <!-- Column: Category -->
        <template #cell(category)="data">
          <b-badge
            variant="light-primary"
            size="sm"
          >
            {{ data.item.product_variant.product.category ? data.item.product_variant.product.category.name : 'Sin categoría' }}
          </b-badge>
        </template>

        <!-- Column: Base Price -->
        <template #cell(base_price)="data">
          <span class="font-weight-bold">Q{{ data.item.base_price_snapshot }}</span>
        </template>

        <!-- Column: Fixed Price -->
        <template #cell(fixed_price)="data">
          <b-form-input
            v-if="priceList.can_edit"
            v-model.number="data.item.fixed_price"
            type="number"
            min="0"
            step="0.01"
            size="sm"
            @change="updatePrice(data.item, 'lista')"
          />
          <span
            v-else
            class="font-weight-bold text-success"
          >Q{{ data.item.fixed_price }}</span>
        </template>

        <!-- Column: indicador de edicion -->
        <template #cell(is_customized)="data">
          <b-badge
            v-if="data.item.is_customized"
            variant="warning"
            :title="data.item.price_updated_at ? `Editado el ${data.item.price_updated_at}` : ''"
          >
            Editado
          </b-badge>
          <b-badge
            v-else
            variant="light-info"
          >
            Precio base
          </b-badge>
        </template>

        <!-- Column: precio ofertado -->
        <template #cell(offer_price)="data">
          <div
            v-if="priceList.can_edit"
            class="offer-price"
          >
            <b-form-input
              v-model="data.item.offer_price"
              type="number"
              min="0"
              step="0.01"
              size="sm"
              placeholder="sin oferta"
              :state="offerMissingPrice(data.item) ? false : null"
              @input="markOfferPending(data.item)"
            />
            <!-- Limpiar la oferta de esta fila (guardada o a medias). -->
            <b-button
              v-if="hasOfferData(data.item)"
              variant="flat-danger"
              size="sm"
              class="btn-icon"
              title="Limpiar oferta"
              @click="clearOffer(data.item)"
            >
              <feather-icon
                icon="XIcon"
                size="14"
              />
            </b-button>
          </div>
          <span
            v-else-if="data.item.offer_price !== null"
            class="font-weight-bold text-danger"
          >Q{{ data.item.offer_price }}</span>
          <small
            v-else
            class="text-muted"
          >&mdash;</small>
        </template>

        <!-- Column: vigencia de la oferta -->
        <template #cell(offer_window)="data">
          <div
            v-if="priceList.can_edit"
            class="offer-window"
          >
            <b-form-input
              v-model="data.item.offer_starts_at"
              type="date"
              size="sm"
              title="Desde"
              :state="offerMissingStart(data.item) ? false : null"
              @focus="openDatePicker"
              @click="openDatePicker"
              @input="markOfferPending(data.item)"
            />
            <b-form-input
              v-model="data.item.offer_ends_at"
              type="date"
              size="sm"
              title="Hasta (vacío = sin vencimiento)"
              @focus="openDatePicker"
              @click="openDatePicker"
              @input="markOfferPending(data.item)"
            />
            <!--
              La oferta se guarda una sola vez, al terminar de armarla: precio y
              fechas en cualquier orden, y un solo aviso al guardar.
            -->
            <b-button
              v-if="data.item.offerPending"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              size="sm"
              class="btn-icon"
              title="Guardar oferta"
              :disabled="data.item.offerSaving"
              @click="updatePrice(data.item, 'oferta')"
            >
              <feather-icon
                icon="CheckIcon"
                size="14"
              />
            </b-button>
            <b-badge
              v-if="data.item.offerPending"
              variant="danger"
              class="text-nowrap"
            >
              Sin guardar
            </b-badge>
            <b-badge
              v-else-if="data.item.offer_status !== 'sin_oferta'"
              :variant="offerStatusVariant(data.item.offer_status)"
              class="text-nowrap"
            >
              {{ offerStatusLabel(data.item.offer_status) }}
            </b-badge>
          </div>
          <div v-else-if="data.item.offer_status !== 'sin_oferta'">
            <b-badge :variant="offerStatusVariant(data.item.offer_status)">
              {{ offerStatusLabel(data.item.offer_status) }}
            </b-badge>
            <small class="d-block text-muted">
              {{ data.item.offer_starts_at }}
              &rarr;
              {{ data.item.offer_ends_at || 'sin vencimiento' }}
            </small>
          </div>
          <small
            v-else
            class="text-muted"
          >&mdash;</small>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-button
            v-if="priceList.can_edit && data.item.is_customized"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="outline-secondary"
            size="sm"
            title="Devolver esta variante al precio base de su estilo"
            @click="resetToBasePrice(data.item.id)"
          >
            <feather-icon
              icon="RotateCcwIcon"
              size="14"
            />
          </b-button>
        </template>
      </b-table>

      <!-- Pagination -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="12"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-if="productVariantsMeta && paginado"
              v-model="currentPage"
              :total-rows="productVariantsMeta.total"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Modal para importar CSV -->
    <b-modal
      id="import-csv-modal"
      v-model="showImportModal"
      title="Importar Precios desde CSV"
      @ok="importCSV"
    >
      <b-form-file
        v-model="csvFile"
        accept=".csv"
        placeholder="Selecciona un archivo CSV..."
        drop-placeholder="Arrastra el archivo aquí..."
      />
      <b-form-text>
        El archivo CSV debe contener las columnas: product_variant_id, fixed_price
      </b-form-text>
    </b-modal>

    <!-- Sidebar de edición -->
    <price-list-edit
      :price-list-id="priceListId"
      :price-list-edit-sidebar-active.sync="showEditSidebar"
      @refresh-data="loadPriceList"
    />
  </div>
</template>

<script>
import axios from '@axios'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import { isAdmin } from '@/utils/auth'
import { planRowSave, isEmptyValue, hasSavedOffer } from './offerRowPlan'
import {
  createSelection, applyTableSelection, rowsToRestore,
  removeFromSelection as quitarDeSeleccion, clearSelection as vaciarSeleccion,
} from './variantSelection'
import { isPaginated, effectivePerPage } from './listPaging'
import PriceListEdit from './PriceListEdit.vue'

export default {
  components: {
    PriceListEdit,
    vSelect,
  },

  directives: {
    Ripple,
  },

  props: {
    priceListId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      priceList: {},
      searchQuery: '',
      productSearchQuery: '',
      searchQueryTimeout: null,
      perPage: 10,
      perPageOptions: [5, 10, 25, 50, 100, 'Todos'],
      currentPage: 1,
      tableIsBusy: true,
      showImportModal: false,
      showEditSidebar: false,
      csvFile: null,

      productVariantsFields: [
        { key: 'product_name', label: 'Producto' },
        { key: 'category', label: 'Categoría' },
        { key: 'base_price', label: 'Precio Base' },
        { key: 'fixed_price', label: 'Precio en la Lista' },
        { key: 'is_customized', label: 'Estado' },
        { key: 'offer_price', label: 'Precio ofertado' },
        { key: 'offer_window', label: 'Vigencia de la oferta' },
        { key: 'actions', label: '' },
      ],

      // Seleccion multiple para aplicar una oferta a varias variantes.
      // Vive fuera de la tabla a proposito: se conserva al cambiar la busqueda,
      // asi se pueden juntar variantes de varios estilos en una misma oferta.
      // Seleccion acumulada entre busquedas (ver variantSelection.js).
      selection: createSelection(),
      isApplyingBulkOffer: false,
      bulkOffer: { price: '', startsAt: '', endsAt: '' },
      isApplyingBulkPrices: false,
      bulkPrices: { base: '', list: '' },
      isAdmin: isAdmin(),

      // Filtro del indicador de edicion. Los valores son texto a proposito:
      // con reduce, vue-select no puede resolver la etiqueta de un null y el
      // control arrancaria en blanco.
      customizedFilter: 'todas',
      customizedOptions: [
        { value: 'todas', label: 'Todas' },
        { value: 'editadas', label: 'Precio editado' },
        { value: 'base', label: 'Precio base' },
        { value: 'ofertadas', label: 'Precio ofertado' },
      ],

      filterIncludedFields: ['product_name'],

      productVariantsData: [],
      productVariantsMeta: null,

      sortBy: 'created_at',
      sortDirection: 'desc',
      sortOptions: [
        { value: 'created_at', text: 'Recién agregado' },
        { value: 'product_name', text: 'Nombre del producto' },
        { value: 'category', text: 'Categoría' },
        { value: 'base_price', text: 'Precio Base' },
        { value: 'fixed_price', text: 'Precio Fijo' },
        { value: 'calculated_price', text: 'Precio Final' },
      ],

    }
  },

  computed: {
    paginado() {
      return isPaginated(this.customizedFilter, this.searchQuery)
    },
    selectedRows() {
      return this.selection.rows
    },
    /** Explica por que la lista esta en solo lectura. */
    editLockHint() {
      if (this.priceList.can_edit) return 'Tu usuario y esta lista tienen la edición habilitada'
      if (!this.priceList.editable) return 'Esta lista está cerrada a edición desde la base de datos'
      return 'Tu usuario no tiene habilitada la edición de listas de precios'
    },
  },

  watch: {
    priceListId: {
      immediate: true,
      handler() {
        if (this.priceListId) {
          this.loadPriceList()
          this.loadProductVariants()
        }
      },
    },
    searchQuery() {
      this.currentPage = 1
      clearTimeout(this.searchQueryTimeout)
      if (!this.searchQuery || this.searchQuery.trim() === '') {
        this.tableIsBusy = true
        this.loadProductVariants()
      } else {
        this.searchQueryTimeout = setTimeout(() => {
          this.tableIsBusy = true
          this.loadProductVariants()
        }, 300)
      }
    },
    currentPage() {
      this.loadProductVariants()
    },
    customizedFilter() {
      this.currentPage = 1
      this.loadProductVariants()
    },
    perPage() {
      this.currentPage = 1
      this.loadProductVariants()
    },
    sortBy() {
      this.currentPage = 1
      this.loadProductVariants()
    },
    sortDirection() {
      this.currentPage = 1
      this.loadProductVariants()
    },
  },

  methods: {
    /**
     * La tabla solo conoce la pagina visible, asi que su evento se reconcilia
     * contra el acumulado: de las filas visibles quedan las que esten marcadas,
     * y las de otras busquedas se conservan intactas.
     */
    /**
     * Abre el calendario nativo al entrar al campo, sin tener que apuntarle al
     * iconito. showPicker exige un gesto del usuario y no existe en todos los
     * navegadores, asi que se ignora cualquier error: el campo sigue siendo
     * escribible a mano.
     */
    openDatePicker(event) {
      const input = event.target
      if (typeof input.showPicker !== 'function') return
      try {
        input.showPicker()
      } catch (e) {
        // El navegador lo rechazo; se escribe la fecha a mano.
      }
    },

    /**
     * Evento de la tabla: de la pagina visible queda lo marcado y lo de otras
     * busquedas se conserva. Mientras se cargan filas nuevas se ignora (la
     * tabla limpia su seleccion y avisa "ninguna"; ver variantSelection.js).
     */
    onRowsSelected(rows) {
      applyTableSelection(this.selection, this.productVariantsData, rows)
    },

    /** Vuelve a marcar en la tabla lo que ya estaba elegido y cierra la sincronizacion. */
    restoreSelection() {
      const tabla = this.$refs.variantsTable
      if (tabla) {
        rowsToRestore(this.selection, this.productVariantsData).forEach(i => tabla.selectRow(i))
      }
      // Los avisos de esas marcas llegan en el mismo ciclo: se sueltan despues.
      this.$nextTick(() => { this.selection.syncing = false })
    },

    selectAllVisible() {
      this.$refs.variantsTable.selectAllRows()
    },

    removeFromSelection(id) {
      quitarDeSeleccion(this.selection, id)

      const tabla = this.$refs.variantsTable
      const i = this.productVariantsData.findIndex(r => r.id === id)
      if (tabla && i !== -1) tabla.unselectRow(i)
    },

    clearSelection() {
      if (this.$refs.variantsTable) this.$refs.variantsTable.clearSelected()
      vaciarSeleccion(this.selection)
    },

    /**
     * Aplica el mismo precio ofertado y la misma vigencia a las filas
     * seleccionadas. Precio vacio retira la oferta de todas ellas.
     */
    /**
     * Precio base y/o precio en la lista para las variantes seleccionadas.
     * Un campo vacio no cambia nada. El base se aplica primero: el precio en la
     * lista que se indique queda como precio propio por encima del nuevo base.
     */
    async applyBulkPrices() {
      if (!this.selectedRows.length) return

      const conBase = this.isAdmin && !this.isEmptyOffer(this.bulkPrices.base)
      const conLista = !this.isEmptyOffer(this.bulkPrices.list)

      if (!conBase && !conLista) {
        this.makeToast('warning', 'Sin cambios', 'Indica el precio base, el precio en la lista o ambos.')
        return
      }

      if (conBase && !(this.bulkPrices.base > 0)) {
        this.makeToast('warning', 'Precio base inválido', 'El precio base debe ser mayor a cero.')
        return
      }

      const estilos = new Set(this.selectedRows.map(r => r.product_variant.product.id)).size
      const partes = []
      if (conBase) {
        partes.push(`precio base Q${this.bulkPrices.base} en ${estilos} ${estilos === 1 ? 'estilo' : 'estilos'} `
          + '(aplica a todas sus tallas y a TODAS las listas donde siguen el precio base)')
      }
      if (conLista) {
        partes.push(`precio en esta lista Q${this.bulkPrices.list} en ${this.selectedRows.length} variantes`)
      }

      try {
        const confirmado = await this.$bvModal.msgBoxConfirm(`¿Aplicar ${partes.join(' y ')}?`, {
          title: 'Aplicar precios',
          size: 'md',
          okVariant: 'primary',
          okTitle: 'SI',
          cancelTitle: 'NO',
          centered: true,
        })
        if (!confirmado) return

        this.isApplyingBulkPrices = true
        const ids = this.selectedRows.map(r => r.product_variant_id)

        if (conBase) {
          await axios.post('price-list-product-variant/bulk-base-price', {
            product_variant_ids: ids,
            base_price: this.bulkPrices.base,
          })
        }

        if (conLista) {
          await axios.post('price-list-product-variant/bulk-assign', {
            price_list_id: this.priceListId,
            product_variant_ids: ids,
            fixed_price: this.bulkPrices.list,
          })
        }

        this.makeToast('success', 'Precios aplicados', `${this.selectedRows.length} variantes actualizadas.`)

        this.bulkPrices = { base: '', list: '' }
        this.clearSelection()
        this.loadProductVariants()
        this.loadPriceList()
      } catch (error) {
        if (error !== 'cancel') this.showErrors(error)
      } finally {
        this.isApplyingBulkPrices = false
      }
    },

    async applyBulkOffer() {
      if (!this.selectedRows.length) return

      const quitando = this.bulkOffer.price === '' || this.bulkOffer.price === null

      if (!quitando && !this.bulkOffer.startsAt) {
        this.makeToast('warning', 'Falta la fecha', 'Una oferta necesita fecha de inicio.')
        return
      }

      // Misma regla que la fila: la oferta debe ser menor al precio en la lista.
      if (!quitando) {
        const noMenores = this.selectedRows
          .filter(r => Number(this.bulkOffer.price) >= Number(r.fixed_price))
          .map(r => `${r.product_variant.sku} (Q${Number(r.fixed_price).toFixed(2)})`)
        if (noMenores.length) {
          this.makeToast(
            'warning',
            'La oferta no es menor al precio de lista',
            `Q${this.bulkOffer.price} no es menor al precio en la lista de: ${noMenores.slice(0, 6).join(', ')}`
              + `${noMenores.length > 6 ? ` y ${noMenores.length - 6} más` : ''}.`,
          )
          return
        }
      }

      const texto = quitando
        ? `¿Quitar la oferta de ${this.selectedRows.length} variantes?`
        : `¿Aplicar Q${this.bulkOffer.price} a ${this.selectedRows.length} variantes?`

      try {
        const confirmado = await this.$bvModal.msgBoxConfirm(texto, {
          title: quitando ? 'Quitar oferta' : 'Aplicar oferta',
          size: 'sm',
          okVariant: quitando ? 'warning' : 'primary',
          okTitle: 'SI',
          cancelTitle: 'NO',
          centered: true,
        })
        if (!confirmado) return

        this.isApplyingBulkOffer = true

        await axios.post('price-list-product-variant/bulk-assign', {
          price_list_id: this.priceListId,
          product_variant_ids: this.selectedRows.map(r => r.product_variant_id),
          offer_price: quitando ? null : this.bulkOffer.price,
          offer_starts_at: quitando ? null : this.bulkOffer.startsAt,
          offer_ends_at: quitando ? null : (this.bulkOffer.endsAt || null),
        })

        this.makeToast(
          'success',
          quitando ? 'Oferta retirada' : 'Oferta aplicada',
          `${this.selectedRows.length} variantes actualizadas.`,
        )

        this.bulkOffer = { price: '', startsAt: '', endsAt: '' }
        this.clearSelection()
        this.loadProductVariants()
        this.loadPriceList()
      } catch (error) {
        if (error !== 'cancel') this.showErrors(error)
      } finally {
        this.isApplyingBulkOffer = false
      }
    },

    offerStatusLabel(status) {
      return {
        vigente: 'Vigente', programada: 'Programada', vencida: 'Vencida',
      }[status] || ''
    },

    offerStatusVariant(status) {
      return {
        vigente: 'light-success', programada: 'light-info', vencida: 'light-secondary',
      }[status] || 'light-secondary'
    },

    async loadPriceList() {
      try {
        const response = await axios.get(`price-list/${this.priceListId}`)
        this.priceList = response.data.data
      } catch (error) {
        this.showErrors(error)
      }
    },

    async loadProductVariants() {
      try {
        this.tableIsBusy = true

        // Solo "Todas" sin busqueda se pagina; lo demas trae todas las
        // coincidencias (ver listPaging.js).
        const params = {
          page: this.paginado ? this.currentPage : 1,
          perPage: effectivePerPage(this.customizedFilter, this.searchQuery, this.perPage),
          sortBy: this.sortBy,
          sortDirection: this.sortDirection,
        }

        if (this.searchQuery && this.searchQuery.trim() !== '') {
          params.search = this.searchQuery.trim()
        }

        if (this.customizedFilter === 'editadas') {
          params.customized = 1
        } else if (this.customizedFilter === 'base') {
          params.customized = 0
        } else if (this.customizedFilter === 'ofertadas') {
          params.estado = 'ofertadas'
        }

        const response = await axios.get(`price-list/${this.priceListId}/product-variants`, {
          params,
        })
        // Filas nuevas: la tabla va a limpiar su seleccion y avisar "ninguna";
        // ese aviso no debe borrar lo elegido en otras busquedas.
        this.selection.syncing = true
        this.productVariantsData = response.data.data
        this.productVariantsMeta = response.data.meta
        this.$nextTick(() => this.restoreSelection())
      } catch (error) {
        this.selection.syncing = false
        this.showErrors(error)
      } finally {
        this.tableIsBusy = false
      }
    },

    isEmptyOffer(value) {
      return isEmptyValue(value)
    },

    // Oferta a medias: precio sin fecha de inicio.
    offerMissingStart(item) {
      return !isEmptyValue(item.offer_price) && !item.offer_starts_at
    },

    // Oferta a medias: fechas sin precio (y sin oferta guardada).
    offerMissingPrice(item) {
      return isEmptyValue(item.offer_price) && Boolean(item.offer_starts_at || item.offer_ends_at)
    },

    // Hay algo de oferta en la fila: guardada o escrita en pantalla.
    hasOfferData(item) {
      return hasSavedOffer(item) || !isEmptyValue(item.offer_price)
        || Boolean(item.offer_starts_at || item.offer_ends_at)
    },

    // Precio ofertado o fechas editados en pantalla: aparece el boton de guardar.
    markOfferPending(item) {
      this.$set(item, 'offerPending', true)
    },

    /**
     * Guarda la fila: 'lista' (precio en la lista, al cambiarlo), 'oferta'
     * (boton guardar de la fila) o 'limpiar' (boton limpiar). La regla vive en
     * offerRowPlan.js.
     */
    async updatePrice(item, origen = 'lista') {
      const plan = planRowSave(item, origen)

      if (!plan.save) {
        if (plan.aviso) this.makeToast('warning', plan.aviso.titulo, plan.aviso.texto)
        return
      }

      const tocaOferta = Object.prototype.hasOwnProperty.call(plan.payload, 'offer_price')

      if (tocaOferta) this.$set(item, 'offerSaving', true)

      try {
        const response = await axios.put(`price-list-product-variant/${item.id}`, plan.payload)

        // El backend decide el indicador comparando contra el precio base vigente.
        const fresh = response.data.data
        this.$set(item, 'is_customized', fresh.is_customized)
        this.$set(item, 'price_updated_at', fresh.price_updated_at)
        this.$set(item, 'base_price_snapshot', fresh.base_price_snapshot)
        this.$set(item, 'offer_status', fresh.offer_status)

        // Solo si se envio la oferta se toma lo guardado; si no, se conserva lo
        // que este escrito en pantalla (por ejemplo una oferta a medias).
        if (tocaOferta) {
          this.$set(item, 'offer_price', fresh.offer_price)
          this.$set(item, 'offer_starts_at', fresh.offer_starts_at)
          this.$set(item, 'offer_ends_at', fresh.offer_ends_at)
          this.$set(item, 'offerPending', false)
        }

        this.loadPriceList()
        const avisos = {
          lista: ['Precio actualizado', 'El precio en la lista se ha actualizado.'],
          oferta: tocaOferta && plan.payload.offer_price === null
            ? ['Oferta retirada', 'La variante quedó sin oferta.']
            : ['Oferta guardada', `${item.product_variant.sku}: Q${fresh.offer_price} desde ${fresh.offer_starts_at}`
              + `${fresh.offer_ends_at ? ` hasta ${fresh.offer_ends_at}` : ' sin vencimiento'}.`],
          limpiar: ['Oferta retirada', 'La variante quedó sin oferta.'],
        }
        this.makeToast('success', ...avisos[origen])
      } catch (error) {
        this.showErrors(error)
        // Revertir el cambio en caso de error
        this.loadProductVariants()
      } finally {
        if (tocaOferta) this.$set(item, 'offerSaving', false)
      }
    },

    // Quita la oferta de una sola fila. Si solo estaba escrita (no guardada),
    // basta con vaciar los campos.
    async clearOffer(item) {
      if (!hasSavedOffer(item)) {
        this.$set(item, 'offer_price', null)
        this.$set(item, 'offer_starts_at', null)
        this.$set(item, 'offer_ends_at', null)
        this.$set(item, 'offerPending', false)
        return
      }

      // msgBoxConfirm resuelve true/false: "NO" o cerrar el dialogo dan false.
      const confirmado = await this.$bvModal.msgBoxConfirm(
        `¿Quitar la oferta de ${item.product_variant.sku}? Vuelve a venderse a su precio de lista.`,
        {
          title: 'Limpiar oferta',
          size: 'sm',
          okVariant: 'warning',
          okTitle: 'SI',
          cancelTitle: 'NO',
          centered: true,
        },
      )
      if (!confirmado) return

      await this.updatePrice(item, 'limpiar')
    },

    /**
     * Una variante no sale de la lista: vuelve al precio base de su estilo y
     * retoma el seguimiento automatico.
     */
    async resetToBasePrice(id) {
      try {
        const confirmado = await this.$bvModal.msgBoxConfirm(
          '¿Devolver esta variante al precio base de su estilo? Volverá a actualizarse sola cuando cambie el precio base.',
          {
            title: 'Volver al precio base',
            size: 'sm',
            okVariant: 'warning',
            okTitle: 'SI',
            cancelTitle: 'NO',
            centered: true,
          },
        )
        if (!confirmado) return

        await axios.delete(`price-list-product-variant/${id}`)
        this.makeToast('success', 'Precio restablecido', 'La variante volvió al precio base de su estilo.')
        this.loadProductVariants()
        this.loadPriceList()
      } catch (error) {
        if (error !== 'cancel') {
          this.showErrors(error)
        }
      }
    },

    async importCSV() {
      if (!this.csvFile) {
        this.makeToast('warning', 'Sin archivo', 'Debes seleccionar un archivo CSV.')
        return
      }

      try {
        const formData = new FormData()
        formData.append('csv_file', this.csvFile)

        await axios.post(`price-list/${this.priceListId}/import-csv`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        this.makeToast('success', 'Importación exitosa', 'Los precios se han importado correctamente.')
        this.loadProductVariants()
        this.csvFile = null
      } catch (error) {
        this.showErrors(error)
      }
    },

    exportToCSV() {
      const csvContent = this.generateCSV()
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `lista_precios_${this.priceListId}_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    generateCSV() {
      const headers = ['product_variant_id', 'product_name', 'variant_name', 'base_price', 'fixed_price', 'calculated_price']
      const rows = this.productVariantsData.map(item => [
        item.product_variant_id,
        item.product_variant.product.name,
        item.product_variant.name,
        item.product_variant.base_price,
        item.fixed_price || '',
        item.calculated_price,
      ])

      return [headers, ...rows].map(row => row.join(',')).join('\n')
    },

    showErrors(error) {
      if (error.response && error.response.data && error.response.data.data) {
        const errors = error.response.data.data
        Object.keys(errors).forEach(field => {
          this.makeToast('danger', 'Error', `${field}: ${errors[field][0]}`)
        })
      } else {
        this.makeToast('danger', 'Error', 'Ha ocurrido un error inesperado.')
      }
    },

    makeToast(variant = null, title = null, message = null) {
      this.$bvToast.toast(message, {
        title,
        variant,
        solid: true,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
/*
 * Los dos selectores de la barra comparten forma: misma altura, etiqueta a la
 * izquierda y ancho fijo. Sin ancho fijo, vue-select se ajusta al contenido y
 * el control cambia de tamaño con cada opción; si además el ancho queda corto,
 * el input de búsqueda se va a una segunda línea y el control crece en alto
 * (era lo que pasaba con "Todos" en 90px).
 */
.toolbar-field {
  display: flex;
  align-items: center;

  &__label {
    margin: 0 0.5rem 0 0;
    white-space: nowrap;
  }

  &__select {
    flex: 0 0 auto;

    &--state {
      width: 190px;
    }

    &--per-page {
      width: 110px;
    }

    ::v-deep .vs__dropdown-toggle {
      min-height: 38px;
      flex-wrap: nowrap;
    }

    // Una sola linea siempre. vue-select deja un campo de busqueda oculto con
    // ancho minimo junto al valor; con un texto largo ("Precio ofertado") ese
    // campo bajaba a una segunda linea y la caja crecia.
    ::v-deep .vs__selected-options {
      flex-wrap: nowrap;
      min-width: 0;
      overflow: hidden;
    }

    ::v-deep .vs__selected {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    ::v-deep .vs__search,
    ::v-deep .vs__search:focus {
      width: 0;
      min-width: 0;
      padding: 0;
      margin: 0;
    }
  }
}

/*
 * Barra de oferta masiva. Los campos van en linea con su etiqueta arriba; sin
 * ancho fijo, bootstrap los estira al 100% y quedan apilados.
 */
/*
  Panel de seleccion multiple: tarjeta blanca con un bloque por accion (oferta,
  precios) y la lista de lo seleccionado. Se apila en pantallas angostas.
*/
.bulk-panel {
  background: #fff;
  border: 1px solid #e3e8ef;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(18, 64, 111, 0.06);
  padding: 20px 22px;

  &__header {
    margin-bottom: 14px;
  }

  &__title {
    color: #12406f;
    font-weight: 700;
    font-size: 1.35rem;
    margin-bottom: 2px;
  }

  &__subtitle {
    color: #6e7891;
    margin-bottom: 0;
  }
}

.bulk-section {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 18px;
  background: #eef3f9;
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 12px;

  &__intro {
    display: flex;
    align-items: center;
    gap: 14px;
    flex: 0 0 360px;
    max-width: 100%;
    padding-right: 18px;
    border-right: 1px solid #d3dce8;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background: #dbe5f1;
    color: #12406f;

    &--small {
      width: 36px;
      height: 36px;
    }
  }

  &__title {
    color: #12406f;
    font-weight: 700;
    margin-bottom: 2px;
  }

  &__text {
    color: #6e7891;
    font-size: 0.9rem;
    margin-bottom: 0;
  }

  &__form {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 14px;
    flex: 1 1 auto;
  }

  &__actions {
    display: flex;
    gap: 10px;
  }
}

.bulk-field {
  display: flex;
  flex-direction: column;

  label {
    color: #3c4b64;
    font-size: 0.9rem;
    margin-bottom: 4px;
    white-space: nowrap;
  }

  .form-control {
    width: 190px;
    background: #fff;
  }
}

.bulk-selected {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  border: 1px solid #e3e8ef;
  border-radius: 10px;
  padding: 14px 18px;

  &__body {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__title {
    color: #12406f;
    font-weight: 700;
    margin: 6px 0 10px;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-height: 150px;
    overflow-y: auto;
  }
}

.bulk-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #e3ecf7;
  color: #12406f;
  border-radius: 8px;
  padding: 6px 10px 6px 14px;
  font-weight: 500;

  &__remove {
    cursor: pointer;
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }
  }
}

// En pantallas angostas el texto del bloque va arriba y los campos debajo.
@media (max-width: 991.98px) {
  .bulk-section__intro {
    flex: 1 1 100%;
    border-right: 0;
    padding-right: 0;
  }

  .bulk-field .form-control {
    width: 160px;
  }
}

.dark-layout {
  .bulk-panel,
  .bulk-selected {
    background: #283046;
    border-color: #3b4253;
  }

  .bulk-section {
    background: #2f3851;

    &__intro {
      border-color: #3b4253;
    }
  }

  .bulk-panel__title,
  .bulk-section__title,
  .bulk-selected__title,
  .bulk-chip {
    color: #d0d2d6;
  }

  .bulk-section__icon,
  .bulk-chip {
    background: #3b4253;
    color: #d0d2d6;
  }

  .bulk-field .form-control {
    background: #283046;
  }
}

/*
  Fila seleccionada: fondo blanco y una barra azul a la izquierda, en lugar del
  gris azulado de BootstrapVue que tapaba etiquetas y textos. El check junto al
  SKU completa el identificador.
*/
.price-list-variants ::v-deep tbody tr.b-table-row-selected {
  background-color: #fff;

  td {
    background-color: #fff;
  }

  td:first-child {
    box-shadow: inset 4px 0 0 #12406f;
  }
}

.dark-layout .price-list-variants ::v-deep tbody tr.b-table-row-selected {
  background-color: #283046;

  td {
    background-color: #283046;
  }
}

/* Precio ofertado + boton de limpiar, dentro de la grilla. */
.offer-price {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Las dos fechas de la oferta, dentro de la grilla. */
.offer-window {
  display: flex;
  align-items: center;
  gap: 4px;

  .form-control {
    width: 140px;
  }
}

.sort-selector {
  width: 150px;
}

.category-filter {
  width: 250px;
}

.table-container {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
